import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingBag, User, Search, Menu, X, Home } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [bagCount, setBagCount] = useState(0);
  const [favCount, setFavCount] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/bag`, { withCredentials: true })
        .then(r => setBagCount(r.data.length)).catch(() => {});
      axios.get(`${API}/favorites/ids`, { withCredentials: true })
        .then(r => setFavCount(r.data.length)).catch(() => {});
    } else {
      setBagCount(0);
      setFavCount(0);
    }
  }, [user, location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navBg = isHome && !scrolled ? 'bg-transparent' : 'bg-white border-b border-[#E5E5E5]';
  const textColor = isHome && !scrolled ? 'text-white' : 'text-[#0A0A0A]';
  const logoColor = isHome && !scrolled ? 'text-white' : 'text-[#0A0A0A]';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">

            {/* Left Nav */}
            <div className="hidden lg:flex items-center gap-10">
              <Link to="/shop" className={`text-xs uppercase tracking-[0.2em] font-medium hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="nav-shop">
                Shop
              </Link>
              <Link to="/designers" className={`text-xs uppercase tracking-[0.2em] font-medium hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="nav-designers">
                Designers
              </Link>
            </div>

            {/* Logo */}
            <Link to="/" className={`font-playfair text-2xl lg:text-3xl font-bold tracking-[0.15em] ${logoColor} hover:text-[#D4AF37] transition-colors`} data-testid="logo">
              HEIR LUXX
            </Link>

            {/* Right Icons */}
            <div className="hidden lg:flex items-center gap-6">
              <button onClick={() => setSearchOpen(true)} className={`hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="search-btn">
                <Search size={18} />
              </button>
              <Link to={user ? "/favorites" : "/auth"} className={`relative hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="nav-favorites">
                <Heart size={18} />
                {favCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0A0A0A] text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {favCount}
                  </span>
                )}
              </Link>
              <Link to={user ? "/bag" : "/auth"} className={`relative hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="nav-bag">
                <ShoppingBag size={18} />
                {bagCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0A0A0A] text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {bagCount}
                  </span>
                )}
              </Link>
              <Link to={user ? "/profile" : "/auth"} className={`hover:text-[#D4AF37] transition-colors ${textColor}`} data-testid="nav-account">
                <User size={18} />
              </Link>
            </div>

            {/* Mobile */}
            <button className={`lg:hidden ${textColor}`} onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-[#E5E5E5] px-6 py-6 flex flex-col gap-5">
            <Link to="/shop" className="text-xs uppercase tracking-[0.2em] font-medium text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setMobileOpen(false)}>Shop</Link>
            <Link to="/designers" className="text-xs uppercase tracking-[0.2em] font-medium text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setMobileOpen(false)}>Designers</Link>
            <Link to={user ? "/favorites" : "/auth"} className="text-xs uppercase tracking-[0.2em] font-medium text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setMobileOpen(false)}>Favorites {favCount > 0 && `(${favCount})`}</Link>
            <Link to={user ? "/bag" : "/auth"} className="text-xs uppercase tracking-[0.2em] font-medium text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setMobileOpen(false)}>Bag {bagCount > 0 && `(${bagCount})`}</Link>
            <Link to={user ? "/profile" : "/auth"} className="text-xs uppercase tracking-[0.2em] font-medium text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setMobileOpen(false)}>Account</Link>
          </div>
        )}
      </nav>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center px-6">
          <button className="absolute top-6 right-8 text-[#0A0A0A] hover:text-[#D4AF37]" onClick={() => setSearchOpen(false)}>
            <X size={24} />
          </button>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 font-semibold">Search</p>
          <form onSubmit={handleSearch} className="w-full max-w-xl">
            <input
              autoFocus
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search designers, products..."
              className="w-full border-b-2 border-[#0A0A0A] focus:border-[#D4AF37] outline-none text-2xl font-playfair py-3 text-center bg-transparent text-[#0A0A0A] placeholder-[#CCCCCC]"
              data-testid="search-input"
            />
            <button type="submit" className="mt-8 mx-auto block bg-[#0A0A0A] text-white hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors px-10 py-3 text-xs uppercase tracking-widest">
              Search
            </button>
          </form>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E5E5E5] flex items-center justify-around px-2 h-16 safe-area-pb" data-testid="mobile-bottom-nav">
        <Link to="/" className={`flex flex-col items-center gap-0.5 py-2 px-3 ${location.pathname === '/' ? 'text-[#D4AF37]' : 'text-[#555555]'}`} data-testid="mobile-nav-home">
          <Home size={20} />
          <span className="text-[9px] uppercase tracking-wider font-work">Home</span>
        </Link>
        <Link to="/shop" className={`flex flex-col items-center gap-0.5 py-2 px-3 ${location.pathname === '/shop' ? 'text-[#D4AF37]' : 'text-[#555555]'}`} data-testid="mobile-nav-shop">
          <Search size={20} />
          <span className="text-[9px] uppercase tracking-wider font-work">Shop</span>
        </Link>
        <Link to={user ? "/favorites" : "/auth"} className={`flex flex-col items-center gap-0.5 py-2 px-3 relative ${location.pathname === '/favorites' ? 'text-[#D4AF37]' : 'text-[#555555]'}`} data-testid="mobile-nav-favorites">
          <Heart size={20} />
          {favCount > 0 && (
            <span className="absolute top-1 right-2 bg-[#D4AF37] text-[#0A0A0A] text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold">{favCount}</span>
          )}
          <span className="text-[9px] uppercase tracking-wider font-work">Saved</span>
        </Link>
        <Link to={user ? "/bag" : "/auth"} className={`flex flex-col items-center gap-0.5 py-2 px-3 relative ${location.pathname === '/bag' ? 'text-[#D4AF37]' : 'text-[#555555]'}`} data-testid="mobile-nav-bag">
          <ShoppingBag size={20} />
          {bagCount > 0 && (
            <span className="absolute top-1 right-2 bg-[#D4AF37] text-[#0A0A0A] text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-bold">{bagCount}</span>
          )}
          <span className="text-[9px] uppercase tracking-wider font-work">Bag</span>
        </Link>
        <Link to={user ? "/profile" : "/auth"} className={`flex flex-col items-center gap-0.5 py-2 px-3 ${location.pathname === '/profile' ? 'text-[#D4AF37]' : 'text-[#555555]'}`} data-testid="mobile-nav-account">
          <User size={20} />
          <span className="text-[9px] uppercase tracking-wider font-work">Account</span>
        </Link>
      </div>
    </>
  );
}
